import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, forkJoin, from, map, mergeMap, Observable, of, throwError, toArray } from 'rxjs';
import { IFileDataSchema, IImportedData } from '../file-transfer-services/interfaces';
import { JsonFileTransfer, jsonImportError } from '../file-transfer-services/jsonFileTransfer.service';
import { IRackingRuleSortItem, IResponseRule, IRule, RackingService } from '../racking.service';
import { FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const fileName = 'RackingRules';
const importConcurrency = 5;

export interface IRackingRuleExportData {
	name: string;
	description?: string;
	batchType?: string;
	useCoatingInOptimization?: boolean;
	material: string;
	machine: string;
	profile?: string;
	groupByProductionRoute?: boolean;
	slotsPerCell: number;
	slotConfiguration: 'MixElements' | 'MixCompleteElements' | 'SeparateElements' | 'GroupSimilarElements';
	productionSequenceNumberMode: 'Disabled' | 'Slave' | 'Master';
	rackset?: string;
	placementPriority?: number | null;
	segregation?: string;
	optimization?: string;
	sorting?: IRackingRuleSortItem[];
	id0?: string;
	id1?: string;
	id2?: string;
	id3?: string;
	id4?: string;
	id5?: string;
	id6?: string;
	id7?: string;
	id8?: string;
	id9?: string;
	state?: 'Any' | 'NoGeometry' | 'OnlyGeometry';
	logisticsFormula?: string;
}

const RackingRuleSortTypeSchema: IFileDataSchema<IRackingRuleSortItem['type']> = {
	name: 'RackingRuleSortType',
	properties: {
		self: { type: 'string', required: true },
	},
};

const RackingRuleSortItemSchema: IFileDataSchema<IRackingRuleSortItem> = {
	name: 'RackingRuleSortItem',
	properties: {
		priority: { type: 'number', required: true },
		order: { type: 'number', required: true },
		type: { type: { schema: RackingRuleSortTypeSchema }, required: true },
	},
};

const RackingRuleSchema: IFileDataSchema<IRackingRuleExportData> = {
	name: fileName,
	properties: {
		name: { type: 'string', required: true },
		description: { type: 'string', required: false },
		batchType: { type: 'string', required: false },
		useCoatingInOptimization: { type: 'boolean', required: false },
		material: { type: 'string', required: true },
		machine: { type: 'string', required: true },
		profile: { type: 'string', required: false },
		groupByProductionRoute: { type: 'boolean', required: false },
		slotsPerCell: { type: 'number', required: false },
		slotConfiguration: { type: 'string', required: false },
		productionSequenceNumberMode: { type: 'string', required: false },
		rackset: { type: 'string', required: false },
		placementPriority: { type: 'number', required: false },
		segregation: { type: 'string', required: false },
		optimization: { type: 'string', required: false },
		sorting: { type: { array: { schema: RackingRuleSortItemSchema } }, required: false },
		id0: { type: 'string', required: false },
		id1: { type: 'string', required: false },
		id2: { type: 'string', required: false },
		id3: { type: 'string', required: false },
		id4: { type: 'string', required: false },
		id5: { type: 'string', required: false },
		id6: { type: 'string', required: false },
		id7: { type: 'string', required: false },
		id8: { type: 'string', required: false },
		id9: { type: 'string', required: false },
		state: { type: 'string', required: false },
		logisticsFormula: { type: 'string', required: false },
	},
};

interface RackingRuleImportContext {
	ruleNameMap: Map<string, IResponseRule>;
	racksetSelfByName: Map<string, string>;
}

/** A row that passed validation, carrying its file location through to a save failure too. */
interface RackingRuleImportCandidate {
	rule: IRule;
	location?: string;
}

@Injectable({
	providedIn: 'root',
})
export class RackingRuleTransferService implements ITransferService<IResponseRule> {
	private translationPrefix = 'masterdata.racking.ruleImportExport';

	constructor(
		private rackingService: RackingService,
		private jsonFileTransfer: JsonFileTransfer<IRackingRuleExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => true;
	public getTemplate = (): void => {
		this.downloadTemplate();
	};

	// Rule names are not unique on the backend, so name-based matching is only safe once we have
	// actively confirmed there is no collision - both among the existing rules and within the file
	// being exported/imported. Nothing here treats name as unique on faith.
	public exportData(rules: IResponseRule[]): Observable<IResponseRule[]> {
		return this.rackingService.getRacksets().pipe(
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
			mergeMap((racksets) => {
				const duplicateNames = findDuplicateNames(rules);
				const duplicateRacksetNames = findDuplicateNames(racksets.items);

				if (duplicateNames.length > 0 || duplicateRacksetNames.length > 0) {
					const messages: string[] = [];
					if (duplicateNames.length > 0) {
						messages.push(this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-names.content`, { names: duplicateNames.join(', ') }));
					}
					if (duplicateRacksetNames.length > 0) {
						messages.push(this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-rackset-names.content`, { names: duplicateRacksetNames.join(', ') }));
					}
					return throwError(() => new Error(messages.join('\n')));
				}

				const racksetNameBySelf = this.buildRacksetNameBySelfMap(racksets.items);
				const data = rules.map((rule) => this.toExportRow(rule, racksetNameBySelf));

				return this.jsonFileTransfer.exportData(fileName, data, RackingRuleSchema).pipe(
					map(() => rules),
					catchError((error) => {
						throw new Error(`Failed to export data: ${error.message}`);
					}),
				);
			}),
		);
	}

	// A blank starting point for import: the same fields exportData produces, with one filled-in
	// example row instead of real data. Unlike setup.ts, none of the enum-shaped fields here
	// (slotConfiguration, productionSequenceNumberMode, state) are actually validated against a
	// fixed set on import - see validateRow, which only rejects a missing name - so a single
	// example row is enough; there's no closed enum list to enumerate.
	public downloadTemplate(): void {
		const exampleRow: IRackingRuleExportData = {
			name: 'Example Rule',
			description: 'Example description',
			batchType: 'Example batch type',
			useCoatingInOptimization: false,
			material: 'Example material',
			machine: 'Example machine',
			profile: 'Example profile',
			groupByProductionRoute: false,
			slotsPerCell: 1,
			slotConfiguration: 'MixElements',
			productionSequenceNumberMode: 'Disabled',
			rackset: '',
			placementPriority: null,
			segregation: '',
			optimization: '',
			sorting: [],
			id0: '',
			id1: '',
			id2: '',
			id3: '',
			id4: '',
			id5: '',
			id6: '',
			id7: '',
			id8: '',
			id9: '',
			state: 'Any',
			logisticsFormula: '(KRMSAML AND KRMFARVE) OR RAMSAML',
		};

		this.jsonFileTransfer.exportData(`${fileName}Template`, [exampleRow], RackingRuleSchema).subscribe();
	}

	private toExportRow(rule: IResponseRule, racksetNameBySelf: Map<string, string>): IRackingRuleExportData {
		return {
			name: rule.name,
			description: rule.description || '',
			batchType: rule.batchType?.self ? this.rackingService.getLastSegment(rule.batchType.self) : '',
			useCoatingInOptimization: rule.useCoatingInOptimization ?? false,
			material: rule.aluminium?.material?.self ? this.rackingService.getLastSegment(rule.aluminium.material.self) : '',
			machine: rule.aluminium?.machine?.self ? this.rackingService.getLastSegment(rule.aluminium.machine.self) : '',
			profile: rule.profile?.self ? this.rackingService.getLastSegment(rule.profile.self) : '',
			groupByProductionRoute: rule.groupByProductionRoute,
			slotsPerCell: rule.slotsPerCell,
			slotConfiguration: rule.slotConfiguration,
			productionSequenceNumberMode: rule.productionSequenceNumberMode,
			rackset: this.resolveRacksetNameForExport(rule, racksetNameBySelf),
			placementPriority: rule.placementPriority,
			segregation: rule.segregation?.identifier || '',
			optimization: rule.optimization?.identifier || '',
			sorting: rule.sorting,
			id0: rule.default?.identifiers[0] ?? '',
			id1: rule.default?.identifiers[1] ?? '',
			id2: rule.default?.identifiers[2] ?? '',
			id3: rule.default?.identifiers[3] ?? '',
			id4: rule.default?.identifiers[4] ?? '',
			id5: rule.default?.identifiers[5] ?? '',
			id6: rule.default?.identifiers[6] ?? '',
			id7: rule.default?.identifiers[7] ?? '',
			id8: rule.default?.identifiers[8] ?? '',
			id9: rule.default?.identifiers[9] ?? '',
			state: rule.default?.state,
			logisticsFormula: rule.logisticsFormula ?? '',
		};
	}

	// Neither the create/edit dialog nor the backend ever check that a rule's material, machine,
	// profile or batchType still exist, so a rackset that no longer resolves isn't treated any
	// differently here - it just exports as a blank name instead of failing the export.
	private resolveRacksetNameForExport(rule: IResponseRule, racksetNameBySelf: Map<string, string>): string {
		if (!rule.rackset?.self) {
			return '';
		}
		return racksetNameBySelf.get(rule.rackset.self) ?? '';
	}

	// Duplicate names (file or roster), per-row validation problems (missing name), and per-row
	// save failures all get discovered and reported together in one pass, instead of a user fixing
	// one category, re-importing, and only then discovering the next. Validating a row only needs
	// the rackset lookup (best-effort, never rejects), not unambiguous rule-name matching, so it's
	// safe to run even when names collide - only the actual create/update is withheld then, since
	// it's the one step that depends on matching a row to exactly one existing rule by name.
	public importData(file: File | File[], _overwrite: boolean = false): Observable<ImportResponse> {
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			toArray(),
			mergeMap((parsedRows) => {
				// Nothing parsed - whatever went wrong is already in ImportErrors. Stop here
				// instead of fetching the roster only to report an unrelated duplicate-name error
				// that would bury the actual parse failure.
				if (parsedRows.length === 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const fileDuplicates = findDuplicateNames(parsedRows.map((imported) => imported.data));

				return forkJoin({
					rules: this.rackingService.getRules(),
					racksets: this.rackingService.getRacksets(),
				}).pipe(
					mergeMap(({ rules, racksets }) => {
						const rosterDuplicates = findDuplicateNames(rules.items);
						const racksetRosterDuplicates = findDuplicateNames(racksets.items);

						if (fileDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-file', fileDuplicates);
						}
						if (rosterDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-roster', rosterDuplicates);
						}
						if (racksetRosterDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-rackset-names', racksetRosterDuplicates);
						}
						const namesAreAmbiguous = fileDuplicates.length > 0 || rosterDuplicates.length > 0 || racksetRosterDuplicates.length > 0;

						const ctx: RackingRuleImportContext = {
							ruleNameMap: buildRuleNameMap(rules.items),
							racksetSelfByName: this.buildRacksetSelfByNameMap(racksets.items),
						};

						return from(parsedRows).pipe(
							filterMap(this.validateRow(ctx, ImportErrors)),
							toArray(),
							mergeMap((validRules) => (namesAreAmbiguous ? of([]) : from(validRules).pipe(mergeMap(this.createOrUpdateRule(ctx, ImportErrors), importConcurrency), toArray()))),
							map(
								(results) =>
									({
										successfulImports: results.map(
											(r) =>
												({
													Id: r.entity.name,
													status: r.status,
												}) as SuccessFullImport,
										),
										errors: ImportErrors,
									}) as ImportResponse,
							),
						);
					}),
				);
			}),
		);
	}

	private importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IImportedData<IRackingRuleExportData>> => {
			const errorList: jsonImportError[] = [];

			try {
				const rows = this.jsonFileTransfer.importData(buffer, RackingRuleSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						message: e.error,
						status: ImportStatus.ParsingError,
					} as FailedParse);
				});

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	// Name is what the rest of this service matches rows against, so a blank-after-trim name is
	// rejected here rather than silently normalized away by findDuplicateNames/normalizeName.
	private validateRow(ctx: RackingRuleImportContext, ImportErrors: ImportErrorTypes[]) {
		return (imported: IImportedData<IRackingRuleExportData>): RackingRuleImportCandidate | undefined => {
			const { data: row, location } = imported;
			const errors: Record<string, ValidationErrors> = {};

			if (!normalizeName(row.name)) {
				errors['name'] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.missing-required-fields`),
				};
			}

			// Same treatment as material/machine/profile/batchType below: an unresolved rackset
			// name isn't rejected, it just leaves the rule without a rackset.
			const racksetSelf = row.rackset ? (ctx.racksetSelfByName.get(normalizeName(row.rackset)) ?? null) : null;

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({
					Id: row.name,
					location,
					status: ImportStatus.ValidationError,
					errors,
				} as FailedValidationImport);
				return undefined;
			}

			const rule: IRule = {
				self: null,
				name: row.name,
				description: row.description || null,
				batchType: row.batchType || null,
				useCoatingInOptimization: row.useCoatingInOptimization ?? false,
				material: row.material,
				machine: row.machine,
				profile: row.profile || null,
				groupByProductionRoute: row.groupByProductionRoute ?? false,
				slotsPerCell: row.slotsPerCell ?? 1,
				slotConfiguration: row.slotConfiguration || 'MixElements',
				productionSequenceNumberMode: row.productionSequenceNumberMode || 'Disabled',
				rackset: racksetSelf,
				placementPriority: row.placementPriority ?? null,
				segregation: row.segregation || null,
				optimization: row.optimization || null,
				sorting: row.sorting ?? [],
				updatedDate: null,
				updatedBy: null,
				id0: row.id0 || null,
				id1: row.id1 || null,
				id2: row.id2 || null,
				id3: row.id3 || null,
				id4: row.id4 || null,
				id5: row.id5 || null,
				id6: row.id6 || null,
				id7: row.id7 || null,
				id8: row.id8 || null,
				id9: row.id9 || null,
				state: row.state || 'Any',
				logisticsFormula: row.logisticsFormula || null,
			};

			return { rule, location };
		};
	}

	private createOrUpdateRule(ctx: RackingRuleImportContext, ImportErrors: ImportErrorTypes[]) {
		return (candidate: RackingRuleImportCandidate) => {
			const { rule, location } = candidate;
			// Safe: importData() has already confirmed neither the file nor the roster has a name
			// collision, so at most one existing rule can match here.
			const existingRule = ctx.ruleNameMap.get(normalizeName(rule.name));

			if (existingRule) {
				rule.self = existingRule.self;
				return this.rackingService.updateRule(rule).pipe(
					map(() => ({ entity: rule, status: ImportStatus.SuccessUpdate })),
					catchError((error) => {
						this.pushSaveFailedError(ImportErrors, rule, location, error);
						return of();
					}),
				);
			}

			return this.rackingService.createRule(rule).pipe(
				map(() => ({ entity: rule, status: ImportStatus.SuccessCreate })),
				catchError((error) => {
					this.pushSaveFailedError(ImportErrors, rule, location, error);
					return of();
				}),
			);
		};
	}

	// Never surface the backend's raw error text (an HttpErrorResponse's message, a status code) -
	// map it to a translated, user-facing message instead. Anything we haven't special-cased falls
	// back to a generic "failed to save" message rather than leaking backend/HTTP details.
	private saveFailedErrorKey(error: unknown): string {
		switch ((error as HttpErrorResponse)?.status) {
			case 400:
				// Only the API validates the formula, so a refused formula is named rather than reported as invalid data.
				return (error as HttpErrorResponse).error?.errors?.logisticsFormula ? 'save-failed-invalid-logistics-formula' : 'save-failed-bad-request';
			case 404:
				return 'save-failed-not-found';
			default:
				return 'save-failed';
		}
	}

	private pushSaveFailedError(ImportErrors: ImportErrorTypes[], rule: IRule, location: string | undefined, error: unknown) {
		ImportErrors.push({
			Id: rule.name,
			location,
			status: ImportStatus.Failed,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${this.saveFailedErrorKey(error)}`, { name: rule.name }),
		});
	}

	private pushDuplicateNamesError(ImportErrors: ImportErrorTypes[], errorKey: 'duplicate-names-in-file' | 'duplicate-names-in-roster' | 'duplicate-rackset-names', names: string[]): void {
		ImportErrors.push({
			status: ImportStatus.ParsingError,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKey}`, {
				names: names.join(', '),
			}),
		} as FailedParse);
	}

	private buildRacksetNameBySelfMap(racksets: { self: string; name: string }[]): Map<string, string> {
		const map = new Map<string, string>();
		racksets.forEach((rackset) => map.set(rackset.self, rackset.name));
		return map;
	}

	private buildRacksetSelfByNameMap(racksets: { self: string; name: string }[]): Map<string, string> {
		const map = new Map<string, string>();
		racksets.forEach((rackset) => map.set(normalizeName(rackset.name), rackset.self));
		return map;
	}
}

function buildRuleNameMap(rules: IResponseRule[]): Map<string, IResponseRule> {
	const map = new Map<string, IResponseRule>();
	rules.forEach((rule) => map.set(normalizeName(rule.name), rule));
	return map;
}

/** Names that appear 2+ times once trimmed/lowercased. Blank names are skipped - a missing name is reported separately as a missing-required-field error, not a collision. Returns the original (non-normalized) spelling of every row involved in a collision, for a message a user can act on. */
function findDuplicateNames(items: { name: string }[]): string[] {
	const firstSeen = new Map<string, string>();
	const duplicates = new Set<string>();

	for (const item of items) {
		const normalized = normalizeName(item.name);
		if (!normalized) {
			continue;
		}

		const original = firstSeen.get(normalized);
		if (original !== undefined) {
			duplicates.add(original);
			duplicates.add(item.name);
		} else {
			firstSeen.set(normalized, item.name);
		}
	}

	return Array.from(duplicates);
}

function normalizeName(name: unknown): string {
	return name === null || name === undefined ? '' : String(name).trim().toLowerCase();
}
